import React from "react";
import { Bot, User } from "lucide-react";
import { cn } from "../../lib/utils";

export const CHAT_ASSISTANT_DISPLAY_NAME = "ToolKit AI";

export const BotAvatar: React.FC = () => (
  <div className="w-8 h-8 rounded-full bg-premium-50 flex items-center justify-center shrink-0 border border-premium-200 mt-0.5 shadow-sm">
    <Bot className="w-4 h-4 text-premium-600" />
  </div>
);

export const UserAvatar: React.FC<{ avatarUrl?: string | null }> = ({
  avatarUrl,
}) => (
  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0 border border-border mt-0.5 shadow-sm overflow-hidden">
    {avatarUrl ? (
      <img src={avatarUrl} alt="You" className="w-full h-full object-cover" />
    ) : (
      <User className="w-4 h-4 text-muted-foreground" />
    )}
  </div>
);

const USER_MARKDOWN_BUBBLE =
  "bg-primary text-primary-foreground rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm leading-relaxed shadow-sm [&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-2 [&_h2]:mb-1 [&_h2:first-child]:mt-0 [&_ul]:my-1 [&_li]:ml-3 [&_code]:bg-white/20 [&_code]:px-1 [&_code]:rounded";

const ASSISTANT_MARKDOWN_BUBBLE =
  "bg-card border border-border text-foreground rounded-2xl rounded-tl-sm px-4 py-2.5 text-sm leading-relaxed shadow-sm [&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-2 [&_h2]:mb-1 [&_h2:first-child]:mt-0 [&_ul]:my-1 [&_li]:ml-3 [&_code]:bg-muted [&_code]:px-1 [&_code]:rounded";

export type ChatMarkdownBubbleProps = {
  variant: "user" | "assistant";
  html: string;
};

export const ChatMarkdownBubble: React.FC<ChatMarkdownBubbleProps> = ({
  variant,
  html,
}) => (
  <div
    className={cn(
      variant === "user" ? USER_MARKDOWN_BUBBLE : ASSISTANT_MARKDOWN_BUBBLE,
    )}
    dangerouslySetInnerHTML={{ __html: html }}
  />
);

export type ChatAssistantMessageRowProps = {
  children: React.ReactNode;
  /** Default matches assistant / welcome rows; typing indicator omits flex-1. */
  contentColumnClassName?: string;
};

export const ChatAssistantMessageRow: React.FC<
  ChatAssistantMessageRowProps
> = ({ children, contentColumnClassName = "space-y-1 flex-1 max-w-[90%]" }) => (
  <div className="flex gap-3 w-full">
    <BotAvatar />
    <div className={contentColumnClassName}>
      <p className="text-[13px] font-semibold text-foreground ml-1">
        {CHAT_ASSISTANT_DISPLAY_NAME}
      </p>
      {children}
    </div>
  </div>
);

export type ChatUserMessageRowProps = {
  userAvatar?: string | null;
  userName?: string;
  children: React.ReactNode;
};

export const ChatUserMessageRow: React.FC<ChatUserMessageRowProps> = ({
  userAvatar,
  userName,
  children,
}) => (
  <div className="flex gap-3 w-full justify-end flex-row-reverse">
    <UserAvatar avatarUrl={userAvatar} />
    <div className="space-y-1 flex-1 flex flex-col items-end max-w-[90%]">
      {userName ? (
        <p className="text-[13px] font-semibold text-foreground mr-1">
          {userName}
        </p>
      ) : null}
      {children}
    </div>
  </div>
);
